/** Read-only sampled solid-overlap evidence from currently loaded CPU geometry.
 * This is deliberately not a clearance solver or an instruction-fidelity verdict.
 */
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from '../../core/SceneObject';
import { inspectionObjects } from './selection';

type V = [number, number, number];
type Triangle = [V, V, V];
interface Solid { triangles: Triangle[]; min: V; max: V; epsilon: number; closed: boolean }
interface Part { path: string; solids: Solid[]; min: V; max: V }
const sub = (a: V, b: V): V => [a[0]-b[0], a[1]-b[1], a[2]-b[2]];
const cross = (a: V, b: V): V => [a[1]*b[2]-a[2]*b[1], a[2]*b[0]-a[0]*b[2], a[0]*b[1]-a[1]*b[0]];
const dot = (a: V, b: V): number => a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
const directions: V[] = [[1,.371,.529],[.231,1,.417],[.313,.619,1]];
const samplingTriangleLimit = 12000000;
class SamplingWorkLimitError extends Error {}

/** Three non-axis-aligned signed ray counts avoid parity cancellation in merged
 * overlapping closed shells. Edge hits/surface points are inconclusive, not inside.
 * Agreement is evidence only: orientation errors, thin features and finite sampling
 * can still miss intersections. Open surfaces are never treated as solids.
 */
function inside(point: V, solid: Solid, budget: { remaining: number }): boolean {
    if (!solid.closed || point.some((v,i)=>v<=solid.min[i]+solid.epsilon || v>=solid.max[i]-solid.epsilon)) return false;
    let agreed = 0;
    for (const direction of directions) {
        let winding = 0, ambiguous = false;
        for (const [a,b,c] of solid.triangles) {
            if (budget.remaining === 0) throw new SamplingWorkLimitError();
            budget.remaining--;
            const e1=sub(b,a), e2=sub(c,a), h=cross(direction,e2), det=dot(e1,h);
            if (Math.abs(det)<1e-12) continue;
            const s=sub(point,a), u=dot(s,h)/det;
            if(u < -1e-9 || u > 1+1e-9) continue;
            const q=cross(s,e1), v=dot(direction,q)/det;
            if(v < -1e-9 || u+v > 1+1e-9) continue;
            const t=dot(e2,q)/det;
            if(t < -solid.epsilon) continue;
            if(t <= solid.epsilon || Math.min(u,v,1-u-v)<1e-9) { ambiguous=true; break; }
            winding += det>0 ? 1 : -1;
        }
        if(ambiguous) continue;
        if(winding===0) return false;
        agreed++;
    }
    return agreed>=2;
}

export interface GeometryOverlapOptions { offset?: number; limit?: number }

export function inspectOverlaps(roots: readonly SceneObject[], options: GeometryOverlapOptions = {}) {
    const objects=inspectionObjects(roots);
    const offset=options.offset??0, limit=options.limit??Infinity;
    if(!Number.isSafeInteger(offset)||offset<0||(options.limit!==undefined&&(!Number.isSafeInteger(limit)||limit<1))) throw new Error('offset must be a nonnegative integer; limit must be a positive integer.');
    const parts: Part[]=[];
    const skipped: {path:string;reason:string}[]=[];
    let totalTriangles=0, totalVertices=0;
    for(const object of objects){
        const solids: Solid[]=[];
        const meshes=object.getOwnedMeshes().filter(m=>m.isEnabled()&&m.isVisible&&m.visibility>0&&m.getTotalVertices()>0);
        for(const mesh of meshes){
            const path=object.getPath();
            if(mesh.skeleton||mesh.morphTargetManager||('thinInstanceCount' in mesh && Number(mesh.thinInstanceCount)>0)) {skipped.push({path,reason:'Skinned, morph-target or thin-instance geometry is unsupported.'});continue;}
            const positions=mesh.getVerticesData('position'), indices=mesh.getIndices();
            if(!positions?.length||!indices?.length){skipped.push({path,reason:'CPU indexed triangles unavailable.'});continue;}
            totalVertices+=positions.length/3;
            if(totalVertices>900000)throw new Error('Selection exceeds 900000 vertices. Inspect smaller assemblies.');
            totalTriangles+=indices.length/3;
            if(totalTriangles>300000)throw new Error('Selection exceeds 300000 triangles. Inspect smaller assemblies.');
            const world=mesh.computeWorldMatrix(true), vertices:V[]=[];
            for(let i=0;i<positions.length;i+=3){const v=Vector3.TransformCoordinates(Vector3.FromArray(positions,i),world);vertices.push([v.x,v.y,v.z]);}
            if(vertices.some(v=>v.some(n=>!Number.isFinite(n)))){skipped.push({path,reason:'Non-finite geometry.'});continue;}
            const min:V=[Infinity,Infinity,Infinity],max:V=[-Infinity,-Infinity,-Infinity];
            for(const v of vertices)for(let k=0;k<3;k++){min[k]=Math.min(min[k],v[k]);max[k]=Math.max(max[k],v[k]);}
            const epsilon=Math.max(1e-8,Math.max(...sub(max,min))*1e-7);
            // GLB primitives often split vertices at normals/UV seams. Weld by
            // world position for the oriented edge-balance check. This accepts
            // closed triangle chains (including merged shells), not only manifolds.
            const key=(v:V)=>v.map((n,k)=>Math.round((n-min[k])/epsilon)).join(',');
            const keys=vertices.map(key), edges=new Map<string,number>(),triangles:Triangle[]=[];
            for(let i=0;i<indices.length;i+=3){
                const ids=[indices[i],indices[i+1],indices[i+2]], [a,b,c]=ids.map(id=>vertices[id]);
                if(!a||!b||!c)throw new Error(`Invalid triangle indices in ${path}.`);
                if(dot(cross(sub(b,a),sub(c,a)),cross(sub(b,a),sub(c,a)))<epsilon**4)continue;
                triangles.push([a,b,c]);
                for(let j=0;j<3;j++){const x=keys[ids[j]],y=keys[ids[(j+1)%3]];if(x===y)continue;const edge=x<y?`${x}|${y}`:`${y}|${x}`;edges.set(edge,(edges.get(edge)??0)+(x<y?1:-1));}
            }
            // Every oriented edge must have a cancelling reverse edge. An open
            // surface cannot support an inside/outside inference from ray counts.
            const closed=triangles.length>0&&[...edges.values()].every(n=>n===0);
            if(!closed)skipped.push({path,reason:'Open or inconsistently oriented triangle surface; solid sampling unavailable for this mesh.'});
            solids.push({triangles,min,max,epsilon,closed});
        }
        if(!solids.length){if(object.src&&object.active&&!meshes.length)skipped.push({path:object.getPath(),reason:'No active loaded triangle geometry.'});continue;}
        const min:V=[Infinity,Infinity,Infinity],max:V=[-Infinity,-Infinity,-Infinity];
        for(const solid of solids)for(let k=0;k<3;k++){min[k]=Math.min(min[k],solid.min[k]);max[k]=Math.max(max[k],solid.max[k]);}
        parts.push({path:object.getPath(),solids,min,max});
    }
    parts.sort((a,b)=>a.path.localeCompare(b.path));
    const candidates:{a:Part;b:Part;min:V;max:V}[]=[];
    for(let i=0;i<parts.length;i++)for(let j=i+1;j<parts.length;j++){
        const a=parts[i],b=parts[j],min=a.min.map((v,k)=>Math.max(v,b.min[k])) as V,max=a.max.map((v,k)=>Math.min(v,b.max[k])) as V;
        const epsilon=Math.max(...a.solids.map(s=>s.epsilon),...b.solids.map(s=>s.epsilon));
        if(min.every((v,k)=>max[k]-v>epsilon*2))candidates.push({a,b,min,max});
    }
    if(offset>candidates.length)throw new Error(`offset exceeds ${candidates.length} candidate pairs.`);
    const budget={remaining:samplingTriangleLimit};
    let unfinishedPair: {a:string;b:string}|null=null;
    const results=[];
    for(const {a,b,min,max} of candidates.slice(offset,offset+limit)){
        let witness:V|null=null;
        try {
            sample: for(const x of [.5,.25,.75])for(const y of [.5,.25,.75])for(const z of [.5,.25,.75]){
                const point=[x,y,z].map((f,k)=>min[k]+f*(max[k]-min[k])) as V;
                if(a.solids.some(s=>inside(point,s,budget))&&b.solids.some(s=>inside(point,s,budget))){witness=point;break sample;}
            }
        } catch(error) {
            if(!(error instanceof SamplingWorkLimitError))throw error;
            unfinishedPair={a:a.path,b:b.path};
            break;
        }
        results.push({a:a.path,b:b.path,status:witness?'sampled_penetration':'inconclusive',overlapBounds:{min,max},...(witness?{interiorWitness:witness}:{})});
    }
    const groupedSkipped = [...new Set(skipped.map(s=>s.reason))].map(reason=>({reason, paths:[...new Set(skipped.filter(s=>s.reason===reason).map(s=>s.path))]}));
    const next=offset+results.length;
    return {method:'27-point overlap-box sampling; signed ray counts agree in at least two directions inside each closed oriented triangle mesh. World-space metres. Welding tolerance: max(1e-8 m, mesh extent * 1e-7).',
            interpretation:'A sampled penetration is evidence to inspect, not automatically an assembly error: intentional engagement and simplified molding can overlap. Inconclusive or absent results do not certify clearance, contact or instruction fidelity. Open/deformed/unloaded geometry may be omitted. No scene changes.',
            objects:parts.length,triangles:totalTriangles,candidatePairs:candidates.length,offset,nextOffset:next<candidates.length?next:null,
            sampling:{triangleTestLimit:samplingTriangleLimit,triangleTests:samplingTriangleLimit-budget.remaining,workLimitReached:unfinishedPair!==null,
                ...(unfinishedPair?{unfinishedPair,guidance:'Completed pair evidence is retained. Sampling did not finish this pair; no clearance verdict is available. Continue at nextOffset for a fresh page budget, or inspect the named pair with targeted surface rays if it exceeds a page budget on its own.'}:{})},
            results,skipped:groupedSkipped};
}
